import React, { useState, useEffect } from 'react';
import { FaList, FaPlus, FaEdit, FaTrash, FaTimes, FaSave } from 'react-icons/fa';
import { addCategory, getAllCategories, updateCategory, deleteCategory } from '../../supabase/database';
import { getCategoryDiscountQuota } from '../../utils/categoryQuota';

const ManajemenKategori = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ nama_kategori: '', deskripsi: '', kuota_diskon: 0 });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editForm, setEditForm] = useState({ nama_kategori: '', deskripsi: '', kuota_diskon: 0 });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const categoryList = await getAllCategories();
      setCategories(categoryList);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await addCategory({
        ...formData,
        kuota_diskon: Number(formData.kuota_diskon) || 0
      });
      setMessage({ type: 'success', text: `Kategori "${formData.nama_kategori}" berhasil ditambahkan.` });
      setFormData({ nama_kategori: '', deskripsi: '', kuota_diskon: 0 });
      fetchCategories();
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal menambahkan kategori.' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setEditForm({
      nama_kategori: category.nama_kategori,
      deskripsi: category.deskripsi || '',
      kuota_diskon: Number(category.kuota_diskon) || getCategoryDiscountQuota(category.nama_kategori) || 0
    });
  };

  const handleSaveEdit = async () => {
    setIsSubmitting(true);
    try {
      await updateCategory(editingCategory, {
        ...editForm,
        kuota_diskon: Number(editForm.kuota_diskon) || 0
      });
      setMessage({ type: 'success', text: 'Kategori berhasil diperbarui.' });
      setEditingCategory(null);
      setEditForm({ nama_kategori: '', deskripsi: '', kuota_diskon: 0 });
      fetchCategories();
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal memperbarui kategori.' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditForm({ nama_kategori: '', deskripsi: '', kuota_diskon: 0 });
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (window.confirm(`Apakah Anda yakin ingin menghapus kategori "${categoryName}"?`)) {
      try {
        await deleteCategory(categoryId);
        setMessage({ type: 'success', text: 'Kategori berhasil dihapus.' });
        fetchCategories();
      } catch (error) {
        setMessage({ type: 'error', text: 'Gagal menghapus kategori.' });
        console.error(error);
      }
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      <div className="flex items-center gap-4 mb-6">
        <FaList className="text-4xl text-purple-500" />
        <h1 className="text-3xl font-bold text-gray-800">Manajemen Kategori Buku</h1>
      </div>

      {/* Form Tambah Kategori */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-bold text-gray-700 mb-4">Tambah Kategori Baru</h2>
        <form onSubmit={handleAddCategory} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input 
              name="nama_kategori" 
              value={formData.nama_kategori} 
              onChange={handleChange} 
              placeholder="Nama Kategori" 
              required 
              className="p-2 border rounded-md" 
            />
            <input 
              name="deskripsi" 
              value={formData.deskripsi} 
              onChange={handleChange} 
              placeholder="Deskripsi (Opsional)" 
              className="p-2 border rounded-md" 
            />
            <div>
              <input 
                name="kuota_diskon"
                type="number"
                min="0"
                max="100"
                value={formData.kuota_diskon}
                onChange={handleChange}
                placeholder="Kuota Diskon (%)"
                className="p-2 border rounded-md w-full" 
              />
              <p className="text-xs text-gray-500 mt-1">Isi 0 jika tidak ada kuota khusus</p>
            </div>
          </div>
          {message.text && (
            <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400"
            >
              <FaPlus /> {isSubmitting ? 'Menambahkan...' : 'Tambah Kategori'}
            </button>
          </div>
        </form>
      </div>

      {/* Daftar Kategori */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <h2 className="text-xl font-bold text-gray-700 p-6">Daftar Kategori</h2>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama Kategori</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deskripsi</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kuota Diskon</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="4" className="text-center p-4">Memuat data...</td></tr>
            ) : (
              categories.map(category => (
                <tr key={category.id}>
                  {editingCategory === category.id ? (
                    <>
                      <td className="px-6 py-4">
                        <input
                          name="nama_kategori"
                          value={editForm.nama_kategori}
                          onChange={handleEditFormChange}
                          className="w-full p-1 border rounded text-sm"
                          required
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          name="deskripsi"
                          value={editForm.deskripsi}
                          onChange={handleEditFormChange}
                          className="w-full p-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          name="kuota_diskon"
                          type="number"
                          min="0"
                          max="100"
                          value={editForm.kuota_diskon}
                          onChange={handleEditFormChange}
                          className="w-24 p-1 border rounded text-sm text-right"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={isSubmitting}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-all duration-200"
                            title="Simpan"
                          >
                            <FaSave />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-all duration-200"
                            title="Batal"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{category.nama_kategori}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{category.deskripsi || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{(category.kuota_diskon ?? getCategoryDiscountQuota(category.nama_kategori) ?? 0)}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-all duration-200"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id, category.nama_kategori)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-all duration-200"
                            title="Hapus"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
            {!loading && categories.length === 0 && (
              <tr><td colSpan="4" className="text-center p-4 text-gray-500">Belum ada kategori di database.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManajemenKategori;
